import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Users, TrendingUp, MapPin, Calendar, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { organizationSchema, aboutPageSchema, breadcrumbSchema } from '../utils/structuredData';

const AboutPage = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={styles.page}>
      <style>{`
        /* === About Page — Mobile Responsive (375px safe) === */

        /* All sections: tighter vertical padding */
        .about-hero-section  { padding: clamp(2.5rem, 6vw, 4rem) 0 !important; }
        .about-section-pad   { padding: clamp(2rem, 5vw, 4rem) 0 !important; }

        /* Hero */
        .about-hero-h1 {
          font-size: clamp(1.75rem, 7vw, 4.5rem) !important;
          letter-spacing: 1px !important;
          word-break: break-word !important;
        }
        .about-hero-sub {
          font-size: clamp(0.9rem, 3.5vw, 1.375rem) !important;
          padding: 0 8px !important;
        }

        /* Section headings */
        .about-section-h2 {
          font-size: clamp(1.5rem, 5.5vw, 3.5rem) !important;
          letter-spacing: 1px !important;
          margin-bottom: 24px !important;
          word-break: break-word !important;
          hyphens: auto !important;
        }

        /* Body text */
        .about-body-text { font-size: clamp(0.875rem, 3.5vw, 1.125rem) !important; line-height: 1.75 !important; }

        @media (max-width: 640px) {
          /* Mission section */
          .about-mission-content {
            flex-direction: column !important;
            text-align: center !important;
            gap: 20px !important;
            padding: 0 8px !important;
          }
          .about-mission-icon {
            width: 72px !important;
            height: 72px !important;
            margin: 0 auto !important;
            flex-shrink: 0 !important;
          }
          .about-mission-h2  { font-size: 24px !important; }
          .about-mission-text { font-size: 15px !important; line-height: 1.7 !important; }

          /* Numbers grid — 2×3 on mobile */
          .about-num-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          .about-num-value { font-size: 36px !important; }

          /* Values grid */
          .about-values-grid { grid-template-columns: 1fr !important; }
          .about-value-card  { padding: 24px 16px !important; }

          /* Contact grid — 2 cols on mobile */
          .about-contact-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          .about-contact-value { font-size: 14px !important; word-break: break-word !important; overflow-wrap: anywhere !important; }

          /* CTA section */
          .about-cta-h2  { font-size: 24px !important; line-height: 1.15 !important; }
          .about-cta-text { font-size: 15px !important; }
          .about-btn {
            font-size: 16px !important;
            padding: 14px 20px !important;
            white-space: normal !important;
            text-align: center !important;
            line-height: 1.3 !important;
          }
        }

        /* Extra small (375px and below) */
        @media (max-width: 400px) {
          .about-contact-grid { grid-template-columns: 1fr !important; }
          .about-num-grid     { gap: 12px !important; }
          .about-num-value    { font-size: 30px !important; }
          .about-section-h2   { font-size: 1.4rem !important; }
        }
      `}</style>
      
      <SEO
        title="About Driven2WIN — 23+ Years of Event Marketing"
        description="Since 2003, Driven2WIN has produced 6,500+ events in all 50 states. Learn about our mission and why 250+ dealerships trust us for guaranteed results."
        keywords="about driven2win, automotive event marketing company, dealership event company history, nationwide event marketing leader, car dealer event specialists"
        canonicalUrl="https://driven2win.com/about"
        structuredData={[aboutPageSchema, organizationSchema, breadcrumbSchema([{name:'Home',url:'https://driven2win.com'},{name:'About',url:'https://driven2win.com/about'}])]}
      />
      
      {/* Hero */}
      <section style={styles.hero} className="about-hero-section">
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <h1 style={styles.heroH1} className="about-hero-h1">
            ABOUT <span style={{color: '#FFC300'}}>DRIVEN2WIN</span>
          </h1>
          <p style={styles.heroSubheadline} className="about-hero-sub">
            23+ Years of Creating HIGH-IMPACT Sales Events That Deliver REAL Results for Dealerships Across America
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section style={styles.storySection} className="about-section-pad">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="about-section-h2">
            OUR <span style={{color: '#E31E24'}}>STORY</span>
          </h2>
          <div style={styles.storyContent}>
            <p style={styles.bodyText} className="about-body-text">
              Since our founding over 23 years ago, Driven2WIN has been on a MISSION: to help dealerships across America pack their lots with qualified buyers and move inventory FAST through professionally executed, high-energy sales events.
            </p>
            <p style={styles.bodyText} className="about-body-text">
              What started as a regional automotive event company has grown into a NATIONWIDE powerhouse serving car & truck, golf cart, motorcycle, ATV, and RV dealers in all 50 states. We've produced over <strong>6,500 events</strong>, generated billions in sales revenue, and built a reputation as the most TRUSTED name in automotive, golf cart, motorcycle, and RV event marketing.
            </p>
            <p style={styles.bodyText} className="about-body-text">
              With over 45 years experience operating New & Used Car Dealerships we know how to operate a dealership successfully and profitably. Generating traffic is key, and Driven2WIN event marketing has been the driving force in the event marketing business.
            </p>
            <p style={styles.bodyText} className="about-body-text">
              Our success comes from a simple philosophy: <strong>RESULTS OVER PROMISES.</strong> We don't just talk about big numbers — we DELIVER them. Every event is backed by our performance guarantee, executed with meticulous attention to detail, and supported by a team that treats your success as our own.
            </p>
            <p style={styles.bodyText} className="about-body-text">
              Today, we're proud to be the go-to event marketing partner for hundreds of dealerships nationwide, trusted by general managers, owners, and sales teams who demand nothing less than EXCEPTIONAL results.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section style={styles.missionSection} className="about-section-pad">
        <div style={styles.container}>
          <div style={styles.missionContent} className="about-mission-content">
            <div style={styles.missionIcon} className="about-mission-icon">
              <Target size={80} color="#FFFFFF" />
            </div>
            <div>
              <h2 style={styles.missionH2} className="about-mission-h2">OUR MISSION</h2>
              <p style={styles.missionText} className="about-mission-text">
                To create EXPLOSIVE sales events that generate MASSIVE traffic, deliver GUARANTEED results, and help dealerships across America dominate their markets through professional execution and proven marketing strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* By The Numbers */}
      <section style={styles.numbersSection} className="about-section-pad">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="about-section-h2">
            BY THE <span style={{color: '#FFFFFF'}}>NUMBERS</span>
          </h2>
          <div className="about-num-grid" style={styles.numbersGrid}>
            <NumberCard icon={<Calendar />} number="23+" label="Years in Business" />
            <NumberCard icon={<TrendingUp />} number="6,500+" label="Events Produced" />
            <NumberCard icon={<MapPin />} number="50" label="States Served" />
            <NumberCard icon={<Users />} number="250+" label="Dealer Partners" />
            <NumberCard icon={<Award />} number="$300K+" label="Avg Event Gross" />
            <NumberCard icon={<Target />} number="98%" label="Client Satisfaction" />
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section style={styles.valuesSection} className="about-section-pad">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="about-section-h2">
            OUR <span style={{color: '#E31E24'}}>VALUES</span>
          </h2>
          <div style={styles.valuesGrid} className="about-values-grid">
            <ValueCard className="about-value-card" title="RESULTS-DRIVEN" description="We measure success by ONE metric: RESULTS. Appointments booked, units sold, gross profit generated. Everything else is just noise." />
            <ValueCard title="GUARANTEED PERFORMANCE" description="We don't make promises we can't keep. Every event is backed by our performance guarantee — if we don't deliver, we keep working until you're satisfied." />
            <ValueCard title="TURNKEY EXECUTION" description="From permits to promotion to setup, we handle EVERYTHING. You focus on selling. We handle the rest." />
            <ValueCard title="PROFESSIONAL EXCELLENCE" description="23+ years of experience means we've seen it all. Our team executes flawlessly, anticipates challenges, and delivers PERFECTION every time." />
            <ValueCard title="NATIONWIDE REACH" description="We're not a local company — we're a NATIONAL powerhouse serving dealers in all 50 states with the same level of excellence everywhere." />
            <ValueCard title="CLIENT SUCCESS" description="Your success is our success. We don't just run events — we build long-term partnerships with dealers who trust us to deliver year after year." />
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section style={styles.contactSection} className="about-section-pad">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="about-section-h2">
            GET IN <span style={{color: '#E31E24'}}>TOUCH</span>
          </h2>
          <div style={styles.contactGrid} className="about-contact-grid">
            <div style={styles.contactItem}>
              <h3 style={styles.contactLabel}>HEADQUARTERS</h3>
              <p style={styles.contactValue} className="about-contact-value">5285 Shad Road, Suite 1619<br />Jacksonville, FL 32257</p>
            </div>
            <div style={styles.contactItem}>
              <h3 style={styles.contactLabel}>PHONE</h3>
              <p style={styles.contactValue} className="about-contact-value">904-638-7200</p>
            </div>
            <div style={styles.contactItem}>
              <h3 style={styles.contactLabel}>FAX</h3>
              <p style={styles.contactValue} className="about-contact-value">904-638-7201</p>
            </div>
            <div style={styles.contactItem}>
              <h3 style={styles.contactLabel}>EMAIL</h3>
              <p style={styles.contactValue} className="about-contact-value">info@driven2win.com</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection} className="about-section-pad">
        <div style={styles.container}>
          <h2 style={styles.ctaH2} className="about-cta-h2">
            Ready to Experience <span style={{color: '#FFC300'}}>THE DRIVEN2WIN DIFFERENCE?</span>
          </h2>
          <p style={styles.ctaText} className="about-cta-text">
            Let's create an event that delivers MASSIVE results for your dealership!
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary} className="about-btn">
            Schedule Your FREE Event Strategy Call
            <ArrowRight size={24} style={{marginLeft: '12px'}} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Components
const NumberCard = ({ icon, number, label }) => (
  <div style={styles.numberCard} className="about-num-card">
    <div style={styles.numberIcon}>
      {React.cloneElement(icon, { size: 48, color: '#E31E24' })}
    </div>
    <div style={styles.numberValue} className="about-num-value">{number}</div>
    <div style={styles.numberLabel}>{label}</div>
  </div>
);

const ValueCard = ({ title, description }) => (
  <div style={styles.valueCard}>
    <h3 style={styles.valueTitle}>{title}</h3>
    <p style={styles.valueDescription}>{description}</p>
  </div>
);

// Styles
const styles = {
  page: {
    paddingTop: '109px',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #1A1F3A 0%, #0F1118 100%)',
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(227, 30, 36, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#E5E5E5',
    maxWidth: '900px',
    margin: '0 auto',
    lineHeight: '1.7',
    position: 'relative',
    zIndex: 1,
  },
  storySection: {
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
    background: '#FFFFFF',
  },
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#1A1F3A',
    textAlign: 'center',
    marginBottom: '48px',
    letterSpacing: '2px',
  },
  storyContent: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  bodyText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    lineHeight: '1.8',
    color: '#333',
    marginBottom: '24px',
  },
  missionSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
  },
  missionContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '48px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  missionIcon: {
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  missionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '48px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  missionText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '20px',
    lineHeight: '1.8',
    color: '#FFFFFF',
    fontWeight: '600',
  },
  numbersSection: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
  },
  numbersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '40px',
  },
  numberCard: {
    textAlign: 'center',
  },
  numberIcon: {
    marginBottom: '20px',
  },
  numberValue: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '56px',
    fontWeight: '700',
    color: '#FFC300',
    marginBottom: '8px',
  },
  numberLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  valuesSection: {
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
    background: '#F5F5F5',
  },
  valuesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(350px, 100%), 1fr))',
    gap: '32px',
  },
  valueCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: 'clamp(1.25rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem)',
    border: '3px solid #E5E5E5',
    borderTop: '6px solid #E31E24',
  },
  valueTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#E31E24',
    marginBottom: '16px',
    letterSpacing: '1px',
  },
  valueDescription: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#666',
  },
  contactSection: {
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
    background: '#FFFFFF',
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
    gap: '48px',
    textAlign: 'center',
  },
  contactItem: {},
  contactLabel: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '20px',
    color: '#999',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  contactValue: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    color: '#1A1F3A',
    fontWeight: '700',
    lineHeight: '1.6',
  },
  ctaSection: {
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    padding: 'clamp(2.5rem, 6vw, 6.25rem) 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '56px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#FFFFFF',
    marginBottom: '48px',
    fontWeight: '600',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#FFFFFF',
    color: '#E31E24',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: 'clamp(12px, 3vw, 20px) clamp(20px, 5vw, 48px)',
    border: 'none',
    borderRadius: '8px',
    textDecoration: 'none',
    whiteSpace: 'normal',
    textAlign: 'center',
    lineHeight: '1.3',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default AboutPage;
